import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/ui/date-input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Movimiento } from "./MovimientosSection";
import { cajaBancosAPI, usuariosAPI, Usuario } from "@/lib/api";
import { dataRefresh } from "@/lib/dataRefresh";
import { toNum } from "@/lib/utils";
// Assuming optionsAPI has employees/users or we use authAPI? 
// Current api.ts doesn't seem to have valid 'users' endpoint enabled yet?
// I'll check api.ts for users list.
// If not available, I might have to keep using mock for users or use a placeholder.
// Checking api.ts for users... 

interface NuevoMovimientoProps {
  onBack: () => void;
  onCreate: (movimiento: Movimiento) => void;
  cierres?: any[];
}

const toLocalYmd = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const cierreFechaYmd = (fecha: unknown) => {
  if (typeof fecha === "string" && /^\d{4}-\d{2}-\d{2}/.test(fecha)) {
    return fecha.slice(0, 10);
  }
  if (fecha instanceof Date && !Number.isNaN(fecha.getTime())) {
    return toLocalYmd(fecha);
  }
  return "";
};

/** API expone `cerrado` (no `finalizado`). Compara YYYY-MM-DD local. */
const isFechaCierreCerrado = (cierres: any[] = [], fechaYmd: string) => {
  return cierres.some(
    (cierre) => cierreFechaYmd(cierre.fecha) === fechaYmd && Boolean(cierre.cerrado),
  );
};

const isTodayCierreCerrado = (cierres: any[] = []) =>
  isFechaCierreCerrado(cierres, toLocalYmd(new Date()));

function looksGenericApiMessage(s: string): boolean {
  const t = s.trim().toLowerCase();
  return (
    !t ||
    t === "bad request" ||
    t === "api error" ||
    t === "error" ||
    t === "dia_cerrado" ||
    t === "usd_sin_disponible"
  );
}

function firstHumanMessage(value: unknown, depth = 0): string {
  if (depth > 4 || value == null) return "";
  if (typeof value === "string") {
    const t = value.trim();
    if (looksGenericApiMessage(t)) return "";
    return t;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const got = firstHumanMessage(item, depth + 1);
      if (got) return got;
    }
    return "";
  }
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    for (const key of ["detail", "message", "fecha", "importe", "error"]) {
      const got = firstHumanMessage(o[key], depth + 1);
      if (got) return got;
    }
    for (const v of Object.values(o)) {
      const got = firstHumanMessage(v, depth + 1);
      if (got) return got;
    }
  }
  return "";
}

function movimientoApiErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== "object") return fallback;
  const e = error as { message?: unknown; details?: unknown; data?: unknown; error?: unknown };
  return (
    firstHumanMessage(e.details) ||
    firstHumanMessage(e.data) ||
    firstHumanMessage(e.message) ||
    firstHumanMessage(e.error) ||
    fallback
  );
}

const getDefaultDate = (cierres: any[] = []) => {
  const today = new Date();
  if (isTodayCierreCerrado(cierres)) {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return toLocalYmd(tomorrow);
  }
  return toLocalYmd(today);
};

export function NuevoMovimiento({ onBack, onCreate, cierres = [] }: NuevoMovimientoProps) {
  const [fecha, setFecha] = useState(getDefaultDate(cierres));
  const [tipoMovimiento, setTipoMovimiento] = useState("");
  const [importe, setImporte] = useState<number>(0);
  const [observaciones, setObservaciones] = useState("");
  const [tiposMovimiento, setTiposMovimiento] = useState<any[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [users, setUsers] = useState<Usuario[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  const [ajuste, setAjuste] = useState(false);

  const selectedTipo = tiposMovimiento.find(
    (t) => t.id?.toString() === tipoMovimiento || t.codigo === tipoMovimiento,
  );
  const requiereUsuario = Boolean(
    selectedTipo?.asociarUsuario ?? selectedTipo?.asociar_usuario,
  );

  useEffect(() => {
    const loadTypes = async () => {
      setLoadingTypes(true);
      try {
        const types = await cajaBancosAPI.listTiposMovimiento();
        setTiposMovimiento(types);
      } catch (e) {
        console.error("Error loading types", e);
        toast.error("Error cargando tipos de movimiento");
      } finally {
        setLoadingTypes(false);
      }
    };

    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const res = await usuariosAPI.list();
        setUsers(res);
      } catch (e) {
        console.error("Error loading users", e);
      } finally {
        setLoadingUsers(false);
      }
    };

    loadTypes();
    loadUsers();
  }, []);

  // For users, we might not have an API ready. 
  // I will check if I can get 'users' from somewhere. 
  // If not, I'll temporarily disable user selection or just use string input if API allows.
  // The API likely expects a user ID or nothing (current user).
  // I should check `createMovimiento` in api.ts.
  /*
    export interface CajaMovimiento {
      id?: number;
      tipoMovimientoId: number;
      monto: number;
      fecha: string;
      descripcion?: string;
      // ...
    }
  */
  // It seems `createMovimiento` expects `tipoMovimientoId`. 
  // And `usuarioId` is likely handled by backend from token.

  const todayCierreCerrado = isTodayCierreCerrado(cierres);

  const handleSubmit = async () => {
    if (!fecha) {
      toast.error("La fecha es requerida");
      return;
    }
    if (isFechaCierreCerrado(cierres, fecha)) {
      toast.error("El cierre de esa fecha ya está cerrado. Elija otra fecha.");
      return;
    }
    if (!tipoMovimiento) {
      toast.error("Debe seleccionar un tipo de movimiento");
      return;
    }
    const tipoSel = tiposMovimiento.find(
      (t) => t.id?.toString() === tipoMovimiento || t.codigo === tipoMovimiento,
    );
    if (
      (tipoSel?.asociarUsuario || tipoSel?.asociar_usuario) &&
      (!selectedUser || selectedUser === "none")
    ) {
      toast.error("Este tipo de movimiento requiere un usuario.");
      return;
    }
    if (!importe || importe <= 0) {
      toast.error("El importe debe ser mayor a 0");
      return;
    }

    try {
      const selectedTipo = tiposMovimiento.find(t => t.id.toString() === tipoMovimiento || t.codigo === tipoMovimiento);

      // Prepare data for API
      // API expects: tipoMovimientoId, monto, fecha, descripcion
      // We select by ID.
      const movimientoData: any = {
        tipo_movimiento: selectedTipo ? selectedTipo.id : parseInt(tipoMovimiento),
        importe: importe,
        fecha: fecha,
        usuario: selectedUser && selectedUser !== 'none' ? toNum(selectedUser) : undefined,
        observaciones: observaciones,
        ajuste,
      };

      await cajaBancosAPI.createMovimiento(movimientoData);

      // Fondos keep-alive: el BE puede crear/actualizar Fondo si el tipo tiene "Registrar en Fondos".
      dataRefresh.emit("fondos");
      toast.success(ajuste ? "Movimiento de ajuste creado" : "Movimiento creado exitosamente");
      onCreate(movimientoData as any);
    } catch (error) {
      console.error("Error creating movimiento:", error);
      toast.error(movimientoApiErrorMessage(error, "Error al crear el movimiento"));
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold">Nuevo Movimiento</h3>

      {todayCierreCerrado && (
        <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
          <div className="flex items-start gap-2">
            <div className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center mt-0.5">
              <span className="text-white text-xs font-bold">!</span>
            </div>
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                El cierre diario de hoy está cerrado
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                La fecha por defecto es mañana. No se pueden generar movimientos con fecha de un día cerrado.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="fecha">Fecha</Label>
            <DateInput
              id="fecha"
              value={fecha}
              onChange={(ymd) => setFecha(ymd)}
            />
          </div>

          <div>
            <Label htmlFor="tipoMovimiento">Tipo de Movimiento</Label>
            <Select
              value={tipoMovimiento}
              onValueChange={(v) => {
                setTipoMovimiento(v);
                const t = tiposMovimiento.find(
                  (row) => row.id?.toString() === v || row.codigo === v,
                );
                if (t?.asociarUsuario || t?.asociar_usuario) {
                  if (selectedUser === "none") setSelectedUser("");
                }
              }}
            >
              <SelectTrigger id="tipoMovimiento">
                <SelectValue placeholder={loadingTypes ? "Cargando..." : "Seleccionar tipo..."} />
              </SelectTrigger>
              <SelectContent>
                {(tiposMovimiento || []).map((tipo) => (
                  <SelectItem key={tipo.id || tipo.codigo} value={tipo.id ? tipo.id.toString() : tipo.codigo}>
                    {tipo.nombre}
                    {tipo.moneda === "USD" || tipo.moneda === "usd" ? " (USD)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedTipo && (selectedTipo.moneda === "USD" || selectedTipo.moneda === "usd") ? (
              <p className="text-xs text-muted-foreground mt-1">
                Dólares: se registra en D/H (USD) del cierre, no en efectivo ARS.
              </p>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="importe">Importe</Label>
            <CurrencyInput
              id="importe"
              value={importe}
              onValueChange={setImporte}
              placeholder="0,00"
            />
          </div>

          <div>
            <Label htmlFor="usuario">
              {requiereUsuario ? "Usuario / Vendedor" : "Usuario / Vendedor (Opcional)"}
            </Label>
            <Select value={selectedUser} onValueChange={setSelectedUser}>
              <SelectTrigger id="usuario">
                <SelectValue
                  placeholder={
                    loadingUsers
                      ? "Cargando..."
                      : requiereUsuario
                        ? "Seleccionar usuario..."
                        : "Cualquier usuario"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {!requiereUsuario && (
                  <SelectItem value="none">Sin usuario específico</SelectItem>
                )}
                {(users || []).map((u) => (
                  <SelectItem key={u.id} value={u.id.toString()}>
                    {u.nombre} {u.apellido}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="observaciones">Observaciones (Opcional)</Label>
          <Textarea
            id="observaciones"
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            placeholder="Observaciones adicionales..."
            rows={3}
          />
        </div>

        <div className="flex items-center justify-between gap-4 py-1">
          <div className="flex-1">
            <Label htmlFor="ajuste" className="cursor-pointer">
              Ajuste
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              {ajuste
                ? "Se guarda este movimiento y otro del mismo importe, en sentido contrario. El neto de caja queda en 0."
                : "Se guarda solo el movimiento cargado."}
            </p>
          </div>
          <Switch id="ajuste" checked={ajuste} onCheckedChange={setAjuste} />
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onBack}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          className="bg-gradient-to-br from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
        >
          Guardar Movimiento
        </Button>
      </div>
    </div>
  );
}